import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import { AuthThirdPartyProviders, iamClient } from '@/api/iam'

export interface AuthState {
  user: {
    id: string
    email: string
    name: string
    roles: Array<{ id: string; name: string }>
    projectId: string | null
    projectIds: string[]
  } | null
  permissions: string[]
  isAuthenticated: boolean
  isLoading: boolean
  isImpersonating: boolean
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>
  loginWithGoogle: (payload: unknown) => Promise<void>
  logout: () => Promise<void>
  getToken: () => Promise<string>
  stopImpersonate: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function identityToState(identity: Record<string, unknown>): AuthState {
  const user = identity.user as {
    id?: string
    email: string
    name: string
    roles: string[]
    projectId?: string
    projectIds?: string[]
    profile?: Record<string, unknown>
  } | undefined
  const profile = user?.profile || {}
  const displayName =
    (profile.fullName as string) ||
    (profile.full_name as string) ||
    (profile.displayName as string) ||
    (profile.name as string) ||
    user?.name ||
    ''
  return {
    user: {
      id: user?.id || '',
      email: user?.email || '',
      name: displayName,
      roles: user?.roles?.map((r: string) => ({ id: r, name: r })) || [],
      projectId: user?.projectId ?? null,
      projectIds: user?.projectIds ?? [],
    },
    permissions: (identity.permissions as string[]) || [],
    isAuthenticated: true,
    isLoading: false,
    isImpersonating: false,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    permissions: [],
    isAuthenticated: false,
    isLoading: true,
    isImpersonating: false,
  })

  useEffect(() => {
    iamClient.auth
      .checkAuth()
      .then(async (identity) => {
        const impersonating = await iamClient.auth.isImpersonating().catch(() => false)
        setState({ ...identityToState(identity as Record<string, unknown>), isImpersonating: !!impersonating })
      })
      .catch(() => {
        setState((s) => ({ ...s, isLoading: false }))
      })
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const identity = await iamClient.auth.login({ username: email, password })
    setState(identityToState(identity as Record<string, unknown>))
  }, [])

  const loginWithGoogle = useCallback(async (payload: unknown) => {
    const identity = await iamClient.auth.thirdPartyLogin({
      provider: AuthThirdPartyProviders.GOOGLE,
      payload,
    })
    setState(identityToState(identity as Record<string, unknown>))
  }, [])

  const logout = useCallback(async () => {
    try {
      await iamClient.auth.logout()
    } finally {
      setState({
        user: null,
        permissions: [],
        isAuthenticated: false,
        isLoading: false,
        isImpersonating: false,
      })
    }
  }, [])

  const stopImpersonate = useCallback(async () => {
    await iamClient.auth.stopImpersonate()
    window.location.href = import.meta.env.BASE_URL
  }, [])

  const getToken = useCallback(async () => {
    return iamClient.auth.getToken()
  }, [])

  return (
    <AuthContext.Provider value={{ ...state, login, loginWithGoogle, logout, getToken, stopImpersonate }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
